import React from 'react'
import Alert from '@mui/material/Alert'
import FormField from 'components/FormField'
import ErrorList from 'components/ErrorList'
import CheckboxSelectField from 'components/CheckboxSelectField'
import { AddJobForm, EditJobForm } from 'types/interfaces'
import AddJobError from 'errors/AddJobError'
import EditJobError from 'errors/EditJobError'
import { round } from 'lodash'
import { NursePaygradeStats } from 'types/interfaces'
import { EstimateCost } from 'types/interfaces'
import { Box, Link, MenuItem, Typography } from '@mui/material'
import { useZendesk } from 'contexts/ZendeskProvider'
import SwitchField from './SwitchField'
import update from 'immutability-helper'
import { DENTAL_NURSE_BONUS_RATE } from 'types/constants'

type JobFormType = AddJobForm | EditJobForm
type JobError = AddJobError | EditJobError

interface JobFormProps {
  form: JobFormType
  error?: JobError
  updateForm: (name: string, value: any) => void
  setForm: (form: JobFormType) => void
  mode: 'add' | 'edit'
  paygradeStats?: NursePaygradeStats | null
  estimatedCost?: EstimateCost | null
  canRequestSpecificNurse?: boolean
  canRequestHygienist?: boolean
}

const JobForm: React.FC<JobFormProps> = ({ 
  form, 
  error, 
  updateForm, 
  setForm, 
  mode, 
  paygradeStats, 
  estimatedCost, 
  canRequestSpecificNurse = true, 
  canRequestHygienist = false 
}) => {
  const { openZendesk } = useZendesk()
  
  const isAddJobForm = (form: JobFormType): form is AddJobForm => {
    return mode === 'add'
  }

  const isAddJobError = (error?: JobError): error is AddJobError => {
    return mode === 'add'
  }

  const updateRole = React.useCallback((value: 'nurse' | 'hygienist') => {
    // specialist requirements are not applicable to hygienists.
    // set them to false if a hygienist is selected.
    const hygienistRequired = value === 'hygienist'
    if (hygienistRequired) {
      setForm(update(form, { 
        hygienist_required: { $set: true },
        sedation_required: { $set: false },
        radiography_required: { $set: false },
        implant_required: { $set: false },
        orthodontics_required: { $set: false },
      }))
    } else {
      setForm(update(form, { 
        hygienist_required: { $set: false },
      }))
    }
  }, [form, setForm])

  const role = React.useMemo(() => form.hygienist_required ? 'hygienist' : 'nurse', [form.hygienist_required])

  const canRequestSpecialist = React.useMemo(() => {
    // TODO: update this once we decided to go live with specialist requests
    return false
  }, [])
  
  return (
    <React.Fragment>
      <CheckboxSelectField 
        onChange={(value) => updateRole(value as 'nurse' | 'hygienist')}
        value={role}
        sx={{ flexDirection: 'row' }}
        options={[{
          value: 'nurse',
          label: 'Dental Nurse',
        }, {
          value: 'hygienist',
          label: canRequestHygienist ? 'Hygienist' : 'Hygienist (Coming soon)',
          disabled: !canRequestHygienist,
        }]} 
        errors={error?.hygienist_required}
      />
      {isAddJobForm(form) && isAddJobError(error) ? (
        <FormField
          name='date'
          label='Date'
          type='date'
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e) => updateForm('date', e.target.value)}
          value={form.date ?? ''}
          errors={error?.date}
        />
      ) : null}
      <FormField
        name='start_time'
        label='Start Time'
        type='time'
        onChange={(e) => updateForm('start_time', e.target.value)}
        value={form.start_time ?? ''}
        errors={error?.start_time}
        InputLabelProps={{
          shrink: true,
        }}
      />
      <FormField
        name='end_time'
        label='End Time'
        type='time'
        onChange={(e) => updateForm('end_time', e.target.value)}
        value={form.end_time ?? ''}
        errors={error?.end_time}
        InputLabelProps={{
          shrink: true,
        }}
      />
      <FormField
        name='lunch_break'
        label='Lunch Break (minutes)'
        type='number'
        onChange={(e) => updateForm('lunch_break', e.target.value)}
        value={form.lunch_break ?? ''}
        errors={error?.lunch_break}
      />
      <FormField
        name='description'
        label='Description'
        multiline
        rows={2}
        placeholder="Include information useful to the nurse, such as parking availability, time of lunch break etc."
        onChange={(e) => updateForm('description', e.target.value)}
        value={form.description ?? ''}
        errors={error?.description}
      />
      <FormField
        select
        name='parking'
        label='Parking'
        onChange={(e) => updateForm('parking', e.target.value)}
        value={form.parking ?? ''}
        errors={error?.parking}
      >
        <MenuItem value='not specified'>Not Specified</MenuItem>
        <MenuItem value='free parking available'>Free Parking Available</MenuItem>
        <MenuItem value='paid parking available'>Paid Parking Available</MenuItem>
        <MenuItem value='no parking available'>No Parking Available</MenuItem>
      </FormField>
      <FormField
        name='headcount'
        label='Number of Nurses Required'
        type='number'
        onChange={(e) => updateForm('headcount', e.target.value)}
        value={form.headcount ?? 1}
        errors={error?.headcount}
      />
      {canRequestSpecialist ? (
        <React.Fragment>
          <Typography variant='h4'>Specialist Requirements</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <SwitchField 
              label='Sedation'
              onChange={(checked) => updateForm('sedation_required', checked)}
              value={form.sedation_required ?? false}
            />
            <SwitchField 
              label='Radiography'
              onChange={(checked) => updateForm('radiography_required', checked)}
              value={form.radiography_required ?? false}
            />
            <SwitchField 
              label='Implant'
              onChange={(checked) => updateForm('implant_required', checked)}
              value={form.implant_required ?? false}
            />
            <SwitchField 
              label='Orthodontics'
              onChange={(checked) => updateForm('orthodontics_required', checked)}
              value={form.orthodontics_required ?? false}
            />
          </Box>
        </React.Fragment>
      ) : null}
      {error?.schema ? (
        <Alert severity='error' variant='filled'><ErrorList errors={error.schema} /></Alert>
      ) : null}
      { paygradeStats && estimatedCost ? (
        <Alert severity='info' variant='filled'>
          <Typography>Your total price: <strong>£{round(estimatedCost.median_total, 2)} per day</strong> ({round(estimatedCost.billable_hours, 1)} hours total)</Typography>
          {estimatedCost.discount ? (
            <Typography>{estimatedCost.discount}</Typography>
          ) : null}
          <Typography variant='caption'>
            Our top priority is to fill this position for you. <br />
            If the process takes longer than expected, we may provide bonus rates (£{DENTAL_NURSE_BONUS_RATE}/hr) to nurses to help ensure your job is filled.
          </Typography>
        </Alert>
      ) : null }
      { canRequestSpecificNurse ? (
        <Link color='secondary' onClick={() => openZendesk()} sx={{ cursor: 'pointer' }} >
          If you want to request a specific nurse, click here to chat to us, we can arrange that for you.
        </Link>
      ) : null }
    </React.Fragment>
  )
}

export default JobForm